import type { IntersectionObserverOptions, observerConfigType } from './types/global';

if (!window?.fireOnVisible) {
    // Stored FireOnVisible CBs for global Testing Call
    const fireStore = new Set<{
        elem: Element;
        cb: Function
    }>();

    window.fireAllVisible = async() => {
        const intersectionDummyEntry = {
            boundingClientRect: {},
            intersectionRatio: 1,
            intersectionRect: null,
            isIntersecting: true,
            rootBounds: 1,
            target: null,
            time: 0
        };

        // Iterate over Set and process each item
        for (const item of fireStore) {
            const { cb, elem } = item;

            // Execute callback
            await cb(elem, intersectionDummyEntry);

            // Remove the processed item from the Set
            fireStore.delete(item);
        }

        return true;
    };

    /**
     * Uses IntersectionObserver to determine when an element is in view.
     * You can use options to adjust the way this works
     *
     * @param {NodeList|Node|String} elems - Set of elements or single element to check for visibility
     * @param {Function} cb - Callback function to be called when requirements are met
     * @param {
     *          loop: boolean,
     *          custom: boolean
     *        } options - Option set to adjust requirements
     * @param {boolean} options.custom -
     * @param {boolean} options.loop - Should the observer be detached after first visibility hit or run again. Default: false
     * @param {number} options.entryRatio - Determines how much of the element needs to be visible to be treated as visible in view. Default: 0 - means all has to be visible
     * @param {Element} options.root - Root element to set margins off
     * @param {string} options.rootMargin - Margin at which the element is determined as visible
     * @param {number} options.threshold - Threshold when to start accepting an element as in viewport
     * @param {boolean} options.checkVisibility - If set to true, will also check if element is visible in terms of drawing. Default: false
     * @param {boolean} options.debug - If set to true, will print entry to console on firing. Default: false
     * @param {Node} options.parentContainer - Execute callback function for all trigger-elements inside this container
     */
    window.fireOnVisible = (
        elems: string | Element | NodeList,
        cb: Function,
        options: IntersectionObserverOptions = {}
    ) => {
        const _options = {
            ignoreIntersection: options.ignoreIntersection || false,
            checkVisibility: options.checkVisibility || false,
            custom: options.custom || false,
            debug: options.debug || false,
            entryRatio: options.entryRatio || 0,
            loop: options.loop || false,
            root: options.root || null,
            rootMargin: options.rootMargin || '0px',
            threshold: options.threshold || 0,
            parentContainer: options.parentContainer || null
        };

        let elements: Element[] = [];

        // If elems is a CSS selector
        if (typeof elems === 'string') {
            elements = Array.from(document.querySelectorAll(elems));
        } else if (elems instanceof NodeList) {
            elements = Array.from(elems) as Element[];
        } else if (elems instanceof Element) {
            elements = [elems];
        }

        if (elements.length) {
            // Add to fireStore
            for (const el of elements) {
                fireStore.add({
                    elem: el,
                    cb
                });
            }

            const io = new IntersectionObserver(
                (entries: IntersectionObserverEntry[]) => {
                    for (const entry of entries) {
                        const elem = entry.target;
                        const item = Array.from(fireStore).find((storeItem) => storeItem.elem === elem);

                        if (!item) {
                            continue;
                        }

                        // The browser doesn't support Intersection Observer v2, falling back to v1 behavior.
                        // @ts-ignore
                        let isVisible = !!entry?.isVisible;

                        if (_options.debug) {
                            console.dir(entry);
                        }

                        if (typeof isVisible === 'undefined') {
                            isVisible = true; // Fallback visibility
                        }

                        const visibilityConditions: boolean =
                            _options.custom || // If custom option is set
                            ((!_options.ignoreIntersection ? entry.isIntersecting : true) && // Entry is already intersection ( Maybe if the element is already in view so it will not trigger intersectionRatio > 0)
                                (_options.checkVisibility ? isVisible : true) && // Entry is visible in // terms of drawing (e.g. display:none is not visible)
                                (_options.entryRatio > 0 ? entry.intersectionRatio > _options.entryRatio : true)); // IntersectionRatio is larger then the set entryRatio

                        if (visibilityConditions) {
                            if (_options.parentContainer === null) {
                                cb(item.elem, entry, io);
                            } else {
                                for (const el of elements) {
                                    cb(el, entry, io);
                                }
                            }

                            if (!_options.loop) {
                                io.unobserve(item.elem);
                            }

                            // Remove processed item from fireStore
                            fireStore.delete(item);
                        }
                    }
                },
                {
                    root: _options.root,
                    rootMargin: _options.rootMargin,
                    threshold: _options.threshold
                }
            );

            const observeTarget = (targetElems: Element[]) => {
                for (const elem of targetElems) {
                    const item = Array.from(fireStore).find((storeItem) => storeItem.elem === elem);

                    if (item) {
                        io.observe(item.elem);
                    }
                }
            };

            if (_options.parentContainer && _options.parentContainer instanceof Element) {
                observeTarget([_options.parentContainer]);
            }
            observeTarget(elements);
        } else {
            console.warn('Call of fireOnVisible without any Node or NodeList will be ignored.');
        }
    };
}

if (!window?.fireOnResize) {
    window.fireOnResize = (elems: NodeList | Node, cb, options) => {
        options = options || {};
        const _options = {
            box: options.box || 'border-box' // Possible values are content-box (the default), border-box, and device-pixel-content-box.
        };

        if (elems) {
            const resizeObserver = new ResizeObserver((entries, observer) => {
                for (const entry of entries) {
                    const targetElem = entry.target; // A reference to the Element or SVGElement being observed.
                    const borderBoxSize = entry.borderBoxSize;
                    const devicePixelContentBoxSize = entry.devicePixelContentBoxSize;

                    /*
                    A DOMRectReadOnly object containing the new size of the observed element when the callback is run. Note that this is better supported than the above two properties, but it is left over from an earlier implementation of the Resize Observer API, is still included in the spec for web compat reasons, and may be deprecated in future versions.
                     */
                    const contentRect = entry.contentRect;

                    const unobserveFunc = () => {
                        observer.unobserve(targetElem);
                    };

                    // Element has resized. Fire Callback
                    window.requestAnimationFrame(() => {
                        cb(targetElem, unobserveFunc, {
                            borderBoxSize,
                            devicePixelContentBoxSize,
                            contentRect
                        });
                    });
                }
                // console.log('Size changed');
            });

            // Iterate over all elems if a list. Otherwise use elems as a single element
            if (elems instanceof NodeList) {
                elems.forEach((elem) => {
                    resizeObserver.observe(elem as Element, _options);
                });
            } else if (elems instanceof Element) {
                resizeObserver.observe(elems, _options);
            }
        } else {
            console.warn('Call of fireOnResize without any Node or NodeList will be ignored.');
        }
    };
}

if (!window?.fireOnNode) {
    const mutationObserverMap = new Map();

    window.fireOnNode = (filterSelector = null, cb, targetNode = document.body) => {
        // Create Observer on first call
        if (!mutationObserverMap.has(targetNode)) {
            const filterCallbackMap = new Map();
            const observer = createMutationObserver(targetNode, filterCallbackMap);

            mutationObserverMap.set(targetNode, {
                mutationObserver: observer,
                filterCallbackMap: filterCallbackMap
            });
        }

        const mutationObserverObj = mutationObserverMap.get(targetNode);
        const filterCallbackMap = mutationObserverObj.filterCallbackMap;
        const callbackArr = filterCallbackMap.get(filterSelector);

        if (Array.isArray(callbackArr)) {
            callbackArr.push(cb);
        } else {
            filterCallbackMap.set(filterSelector, [cb]);
        }
    };

    const createMutationObserver = (
        targetNode: Node,
        filterCallbackMap: Map<string, Function[]>,
        options: observerConfigType = {
            attributes: false, // Set to true if mutations to target's attributes are to be observed.
            childList: true, // Set to true if additions and removals of the target node's child elements (including text nodes) are to be observed.
            characterData: false, // Set to true if mutations to target's data are to be observed.
            subtree: true // Set to true if mutations to not just target, but also target's descendants are to be observed.
        }
    ) => {
        const mutationObserver = new MutationObserver((mutationList) => {
            for (const mutation of mutationList) {
                if (mutation.type === 'childList') {
                    const addedNodes = mutation.addedNodes;

                    // Execute callbacks with filters
                    addedNodes.forEach((node) => {
                        // Should prevent TEXT Nodes
                        if (node instanceof HTMLElement) {
                            filterCallbackMap.forEach((cbArray: Function[], filterSelector: string) => {
                                if (node.matches(filterSelector)) {
                                    cbArray.forEach((cb) => typeof cb === 'function' && cb(node));
                                }

                                const matchingNodes = node.querySelectorAll(filterSelector);

                                matchingNodes.forEach((n) => {
                                    cbArray.forEach((cb) => typeof cb === 'function' && cb(n));
                                });
                            });
                        }
                    });
                }
            }
        });
        // Notify me of everything!
        const observerConfig = {
            attributes: options.attributes, // Set to true if mutations to target's attributes are to be observed.
            childList: options.childList, // Set to true if additions and removals of the target node's child elements (including text nodes) are to be observed.
            characterData: options.characterData, // Set to true if mutations to target's data are to be observed.
            subtree: options.subtree // Set to true if mutations to not just target, but also target's descendants are to be observed.
        };

        // Node, config
        // In this case we'll listen to all changes to body and child nodes
        mutationObserver.observe(targetNode, observerConfig);

        return mutationObserver;
    };
}

/**
 * Prevent page scrolling
 *
 * Because of ability of multiple widgets to lock page scrolling,
 * therefore is 'alreadyLocked' value to determine the amount of optionally subsequent locks
 */
if (!window?.bodyScrollLock) {
    window.bodyScrollLock = {
        bodyLock: function(lock) {
            if (lock) {
                // Calculate the width of the scrollbar to avoid background width jumping
                const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

                document.body.style.paddingRight = `${scrollbarWidth}px`;

                document.body.classList.add('noscroll');
            } else {
                document.body.style.paddingRight = '';
                document.body.classList.remove('noscroll');
            }
        }
    };
}

/**
 * Check if browser supports the ability for (smooth) scrolling with CSS
 *
 * @type {boolean}
 */
window.isSmoothScrollSupported = 'scrollBehavior' in document.documentElement.style;

/**
 * Check if client device using Android OS
 *
 * @type {boolean}
 */
window.isAndroidPhone = window.isAndroid && window.isMobileDetected;

/**
 * Scroll to an target element
 *
 * @param {HTMLElement} target - target element (scroll to this element)
 * @param {HTMLElement} scrollContainer - scroll container
 * @param {int} offset - offset value (pixel) for the scroll position
 */
if (!window?.scrollToTarget) {
    window.scrollToTarget = (target, scrollContainer = window, offset = 0) => {
        if (target) {
            let targetPosition = 0;

            if (scrollContainer === window) {
                targetPosition = target.getBoundingClientRect().top + scrollContainer.scrollY;
            } else {
                targetPosition = target.offsetTop;
            }

            if (window.isSmoothScrollSupported && !window.isAndroidPhone) {
                window.requestAnimationFrame(() => {
                    scrollContainer.scrollTo({
                        top: targetPosition + offset,
                        behavior: 'smooth'
                    });
                });
            } else {
                if (typeof scrollContainer.scrollTo === 'function') {
                    scrollContainer.scrollTo(0, targetPosition + offset);
                } else {
                    scrollContainer.scrollTop = targetPosition + offset;
                }
            }
        }
    };
}

if (!window?.addGlobalEventListener) {
    /**
     * Adds global events to listen for later added elements
     * @param event
     * @param selector
     * @param callback
     */
    window.addGlobalEventListener = (event, selector, callback) => {
        document.addEventListener(
            event,
            function(e) {
                const delegationNode = this as Node;
                const target = e.target as Element | null;
                const parentNode = target?.parentNode;

                // loop parent nodes from the target to the delegation node
                for (target; target && target !== delegationNode; parentNode) {
                    if (target.matches(selector)) {
                        callback.call(target, e);
                        break;
                    }
                }
            },
            false
        );
    };
}

if (!window?._createElementWithOptions) {
    /**
     *
     * @param {string} tag
     * @param {Object?} attributes Key Value store of attributes
     * @param {Array|string?} classes
     * @returns {Element}
     */
    window._createElementWithOptions = (tag, attributes, classes) => {
        const el = document.createElement(tag);

        if (attributes) {
            Object.entries(attributes).forEach((keyValue) => {
                const attr = keyValue[0];
                const value = keyValue[1];

                el.setAttribute(attr, value);
            });
        }

        if (classes) {
            if (typeof classes === 'string') {
                classes = classes.split(' ').map(function(cssClass) {
                    return cssClass;
                });
            }

            classes.forEach((cssClass) => {
                el.classList.add(cssClass);
            });
        }
        return el;
    };
}

if (!window?._createElementWithString) {
    /**
     * // NOTE: Maybe switch to createContextualFragment
     * @param {string} htmlString
     *
     * @returns {Node}
     */
    window._createElementWithString = (htmlString) => {
        const wrapper = document.createElement('div');

        wrapper.innerHTML = htmlString;

        return wrapper.firstChild;
    };
}
